'use client'

import { useAtom, useAtomValue } from 'jotai'
import { ArrowUp, AudioLines, ChevronDown, Loader2, Sparkles, SquarePen, X } from 'lucide-react'
import { useTranslations } from 'next-intl'
import {
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Action } from '@/components/elements/actions'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ExchangeIcon } from '@/components/widget/exchange-icon'
import { OverlayScrollY } from '@/components/widget/overlay-scroll-y'
import { LinkingPageContext } from '@/features/dashboard/lib/linking-page-context'
import { MessageActions } from '@/features/order/chat/components/message-actions'
import { LiveChoicePicker } from '@/features/order/choice-picker/components/live-choice-picker'
import { useOrderPrompt } from '@/features/order/order-form/hooks/use-order-prompt'
import { useTypewriterPlaceholder } from '@/features/order/order-form/hooks/use-typewriter-placeholder'
import {
  type ActiveMention,
  findActiveMention,
  removeMention,
} from '@/features/order/order-form/lib/prompt-mention'
import { useCardAccountContext } from '@/hooks/use-card-account'
import { useMicLevel } from '@/hooks/use-mic-level'
import globalStore from '@/lib/configs/store-config'
import { tradeVenueToIconKey } from '@/lib/proto/util'
import { cn } from '@/lib/utils'
import { isOrderEligibleAccount } from '@/lib/utils/account-select'
import { useUserAccounts } from '@/service/account.hook'
import { useSubscribeSTT } from '@/service/ws/use-scribe-stt'
import { composerPrefillRequestAtom } from '@/state/chat'
import { accountAlignmentSelectionAtomFamily } from '@/state/linking-group'
import { orderFormPromptFillAtomFamily } from '@/state/order'
import type { Account } from '@/types/generated/common/account'

/** The field's one-line box: py-0.5 + leading-5. */
const SINGLE_LINE_HEIGHT_PX = 24
const MAX_MENTION_ROWS = 8
/** The trailing mic/send button — the assistant composer's emphasis circle at widget size. */
const TRAILING_BUTTON_BASE =
  'flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full'
const TRAILING_BUTTON_CLASS = cn(TRAILING_BUTTON_BASE, 'bg-emphasis text-emphasis-foreground')
// Idle mic is a quiet ghost, so the live take (solid cyan, below) is unmistakably "on".
const MIC_IDLE_CLASS = cn(
  TRAILING_BUTTON_BASE,
  'text-foreground/60 hover:bg-foreground/10 hover:text-foreground transition-colors'
)
const MIC_LIVE_CLASS = cn(TRAILING_BUTTON_BASE, 'bg-ox-cyan text-ox-ink')

// Driven by `--mic-level` (see useMicLevel in the component): a cyan halo that swells with
// the voice, and a stop button that breathes with it.
const DICTATION_GLOW_STYLE: CSSProperties = {
  boxShadow:
    '0 0 calc(4px + var(--mic-level, 0) * 18px) calc(var(--mic-level, 0) * 3px) color-mix(in oklab, var(--color-ox-cyan) 45%, transparent)',
  transition: 'box-shadow 80ms linear',
}
const MIC_LIVE_STYLE: CSSProperties = {
  transform: 'scale(calc(1 + var(--mic-level, 0) * 0.18))',
  transition: 'transform 80ms linear',
}

/** composerPrefillRequestAtom bucket for the prompt bar of Order Form `cardId`. */
export function orderPromptPrefillKey(cardId: string): string {
  return `order-prompt:${cardId}`
}

/**
 * One-line "describe a trade" field above the Order Form. Always trades: the order is drafted
 * into the form below and the user still reviews and submits. Research lives in the assistant.
 *
 * `@` picks the account the prompt trades under: a picker of order-eligible accounts opens at
 * the caret, and the choice becomes a chip in the field. It applies to this bar's prompts only
 * (sent as a per-message override) — the card's own account selection is left alone.
 */
export function OrderPromptBar({ cardId }: { cardId: string }) {
  const t = useTranslations('orderForm')
  const { submit, isPending, status, feedback, dismiss, startNewSession, hasSession } =
    useOrderPrompt(cardId)
  const { tradingAccountId, tradeVenue } = useCardAccountContext(cardId)
  const { data: accountsData } = useUserAccounts()
  const eligibleAccounts = useMemo(
    () => (accountsData?.accounts ?? []).filter(isOrderEligibleAccount),
    [accountsData?.accounts]
  )
  // Follow the chart's account, including tab pins updated by the page's Align command.
  // Existing ticket rows and remembered order accounts must not mask that selection here.
  const cardAccount =
    eligibleAccounts.find((account) => account.accountId === tradingAccountId) ?? null
  const pageId = useContext(LinkingPageContext)
  const alignment = useAtomValue(accountAlignmentSelectionAtomFamily(pageId), {
    store: globalStore,
  })
  const promptFill = useAtomValue(orderFormPromptFillAtomFamily(cardId), { store: globalStore })
  const [seenFillNonce, setSeenFillNonce] = useState(promptFill.nonce)
  const [clearedAlignmentRevision, setClearedAlignmentRevision] = useState(0)
  const didFill = seenFillNonce !== promptFill.nonce
  const alignmentRevision = alignment?.revision ?? 0
  const activeAlignmentRevision = didFill ? alignmentRevision : clearedAlignmentRevision
  const alignedAccount =
    alignment && alignment.revision > activeAlignmentRevision
      ? (eligibleAccounts.find((account) => account.accountId === alignment.accountId) ?? null)
      : null
  const defaultAccount = alignedAccount ?? cardAccount
  const contextKey = `${cardId}:${tradeVenue}:${tradingAccountId}:${alignmentRevision}:${promptFill.nonce}`
  const [mentionContextKey, setMentionContextKey] = useState(contextKey)
  const [mentionAccount, setMentionAccount] = useState<Account | null>(null)
  // Manual picks yield to the next chart/Align event. Align itself survives chart changes.
  // A filled ticket consumes both priorities so the next prompt starts from chart context.
  if (didFill) {
    setSeenFillNonce(promptFill.nonce)
    setClearedAlignmentRevision(alignmentRevision)
  }
  if (mentionContextKey !== contextKey) {
    setMentionContextKey(contextKey)
    setMentionAccount(null)
  }
  const effectiveMentionAccount =
    (mentionContextKey === contextKey ? mentionAccount : null) ?? defaultAccount
  const clearMention = () => setMentionAccount(null)

  const [text, setText] = useState('')
  // One-shot text from elsewhere (the command palette's "Trade: …"); put in, not sent.
  const [prefill, setPrefill] = useAtom(composerPrefillRequestAtom)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    if (prefill?.storageKey !== orderPromptPrefillKey(cardId)) return
    setText(prefill.text)
    setPrefill(null)
    // Focus it (and bring a docked widget into view) so Enter sends right away. Deferred a
    // frame: the palette is still closing and restoring focus to where it was opened.
    requestAnimationFrame(() => {
      inputRef.current?.focus()
      inputRef.current?.scrollIntoView({ block: 'nearest' })
    })
  }, [cardId, prefill, setPrefill])
  const hasText = text.trim().length > 0
  const [isMultiline, setIsMultiline] = useState(false)
  // Re-measured on every text change — typing, a prefill, or the clear after sending — so the
  // pill returns to rounded-full once the field is back to one line.
  useLayoutEffect(() => {
    const el = inputRef.current
    if (el) setIsMultiline(el.scrollHeight > SINGLE_LINE_HEIGHT_PX * 1.5)
  }, [text])
  const refocusRef = useRef(false)
  // A typed line break (Shift+Enter) turns the pill into a text box: the leading icon steps
  // aside so the text gets the full width. Keyed on an explicit newline, not on soft wrapping —
  // hiding the icon widens the field, which could unwrap the text, bring the icon back and wrap
  // it again, flickering.
  const hasLineBreak = text.includes('\n')
  // The mentioned account sits inline at the start of the text: the chip overlays the field's
  // first line and the textarea indents that line by the chip's width (+ a gap).
  const chipRef = useRef<HTMLSpanElement>(null)
  const [chipIndent, setChipIndent] = useState(0)
  useLayoutEffect(() => {
    setChipIndent(chipRef.current ? chipRef.current.offsetWidth + 6 : 0)
  }, [effectiveMentionAccount?.accountId, effectiveMentionAccount?.name, mentionAccount])

  // The `@query` at the caret, re-read on every edit and caret move.
  const [mention, setMention] = useState<ActiveMention | null>(null)
  const [highlight, setHighlight] = useState(0)
  const candidates = useMemo(() => {
    if (!mention) return []
    const query = mention.query.toLowerCase()
    return eligibleAccounts
      .filter((a) => a.name.toLowerCase().includes(query))
      .slice(0, MAX_MENTION_ROWS)
  }, [eligibleAccounts, mention])
  const readMention = (el: HTMLTextAreaElement) => {
    const next = findActiveMention(el.value, el.selectionStart ?? el.value.length)
    setMention(next)
    if (next?.query !== mention?.query) setHighlight(0)
  }

  const pickAccount = (picked: Account) => {
    if (mention) {
      const next = removeMention(text, mention)
      setText(next.text)
      requestAnimationFrame(() => {
        inputRef.current?.focus()
        inputRef.current?.setSelectionRange(next.caret, next.caret)
      })
    }
    setMentionAccount(picked)
    setMention(null)
  }

  // Voice input — the same ElevenLabs Scribe realtime STT the assistant composer dictates with
  // (useSttDictationAdapter), driven directly since this bar isn't an assistant-ui composer.
  // The live partial shows in the field as it's spoken; the first committed transcript lands
  // after whatever was already typed and ends the take (same as the composer).
  const stt = useSubscribeSTT()
  const [isDictating, setIsDictating] = useState(false)
  // Live input level → `--mic-level` (0–1) on the pill, which drives its glow and the stop
  // button's pulse, so a take visibly reacts to speech instead of sitting still until the
  // transcript commits. Written straight to the DOM: it changes every frame.
  const formRef = useRef<HTMLFormElement>(null)
  useMicLevel(isDictating, (level) =>
    formRef.current?.style.setProperty('--mic-level', level.toFixed(3))
  )
  const dictationRef = useRef({ baseText: '', cursor: 0 })
  const joinSpoken = (spoken: string) =>
    [dictationRef.current.baseText.trimEnd(), spoken.trim()].filter(Boolean).join(' ')
  const startDictation = () => {
    dictationRef.current = { baseText: text, cursor: stt.transcripts.length }
    setIsDictating(true)
    void stt.start()
  }
  const stopDictation = () => {
    setIsDictating(false)
    stt.stop()
  }
  useEffect(() => {
    if (isDictating && stt.partial) setText(joinSpoken(stt.partial))
  }, [isDictating, stt.partial])
  useEffect(() => {
    if (!isDictating || stt.transcripts.length <= dictationRef.current.cursor) return
    setText(joinSpoken(stt.transcripts[stt.transcripts.length - 1].text))
    stopDictation()
    requestAnimationFrame(() => inputRef.current?.focus())
    // eslint-disable-next-line react-hooks/exhaustive-deps -- joinSpoken/stopDictation are stable in effect
  }, [isDictating, stt.transcripts])
  useEffect(() => {
    if (stt.error) setIsDictating(false)
  }, [stt.error])
  // The connection closing on its own (network drop, server timeout) ends the take too, or the
  // stop button would stay up with nothing listening. Only after it was up: `start()` flips
  // isConnected true once the socket opens, so the gap before that isn't a close.
  const sttWasConnectedRef = useRef(false)
  useEffect(() => {
    if (stt.isConnected) {
      sttWasConnectedRef.current = true
      return
    }
    if (isDictating && sttWasConnectedRef.current) setIsDictating(false)
    sttWasConnectedRef.current = false
  }, [isDictating, stt.isConnected])
  // Closing the widget mid-take must release the microphone.
  const stopStt = stt.stop
  useEffect(() => stopStt, [stopStt])

  // The placeholder types through a lead-in and a few examples. The account chip already says
  // who trades, so the examples don't name one; any too wide for the line beside the chip are
  // skipped rather than wrapping the pill. It stops once there's text or a take running.
  const placeholderPhrases = [
    t('promptBar.placeholder'),
    t('promptBar.examples.twap'),
    t('promptBar.examples.limit'),
    t('promptBar.examples.close'),
  ]
  useTypewriterPlaceholder(inputRef, placeholderPhrases, {
    active: !hasText && !isDictating,
    // Before the first partial arrives, say the take is live rather than leave the field blank.
    idle: isDictating ? t('promptBar.listening') : t('promptBar.placeholder'),
  })

  const send = async () => {
    if (!hasText || isPending || isDictating) return
    refocusRef.current = true
    // The chip stays after sending, so a follow-up ("make it 0.2") trades the same account.
    if (await submit(text, { account: effectiveMentionAccount })) setText('')
  }

  // The field is disabled (so blurred) while the backend answers; hand focus back once it's
  // editable again so a follow-up ("1 hour") can be typed straight away.
  useEffect(() => {
    if (isPending || !refocusRef.current) return
    refocusRef.current = false
    inputRef.current?.focus()
  }, [isPending])

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    void send()
  }

  // Enter sends, Shift+Enter breaks the line (same as the assistant composer). Skip while an
  // IME is composing, or confirming a Korean/Japanese candidate would send half a word. While
  // the @ picker is open, arrows move through it and Enter/Tab pick the highlighted account.
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.nativeEvent.isComposing) return
    if (mention && candidates.length > 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const step = e.key === 'ArrowDown' ? 1 : -1
        setHighlight((i) => (i + step + candidates.length) % candidates.length)
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        pickAccount(candidates[Math.min(highlight, candidates.length - 1)])
        return
      }
    }
    if (mention && e.key === 'Escape') {
      e.preventDefault()
      setMention(null)
      return
    }
    // Backspace at the very start takes the chip off, like deleting a token.
    if (
      e.key === 'Backspace' &&
      mentionAccount &&
      e.currentTarget.selectionStart === 0 &&
      e.currentTarget.selectionEnd === 0
    ) {
      e.preventDefault()
      clearMention()
      return
    }
    if (e.key !== 'Enter' || e.shiftKey) return
    e.preventDefault()
    void send()
  }

  return (
    <div data-tutorial="prompt" className="border-foreground/10 shrink-0 border-b px-3 py-2">
      <div className="relative">
        {/* The assistant composer's pattern at widget density: a one-row pill (icon, field,
            send) that grows as Shift+Enter adds lines, softening to rounded-3xl once it's taller
            than one line. */}
        <form
          ref={formRef}
          onSubmit={onSubmit}
          onClick={() => inputRef.current?.focus()}
          className={cn(
            'flex cursor-text items-end gap-2 border py-1 pr-1 pl-3 transition-[border-radius,border-color]',
            isDictating
              ? 'border-ox-cyan/60'
              : 'border-foreground/15 focus-within:border-foreground/40 hover:border-foreground/30',
            isMultiline ? 'rounded-3xl' : 'rounded-full'
          )}
          style={isDictating ? DICTATION_GLOW_STYLE : undefined}
        >
          {hasLineBreak ? null : isPending ? (
            <Loader2 className="text-foreground/40 mb-1.5 size-3.5 shrink-0 animate-spin" />
          ) : hasSession && !isDictating ? (
            // A draft session is open: this slot becomes "New order", dropping it (the next
            // prompt then starts from scratch rather than refining the current draft).
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                startNewSession()
                setText('')
                setMentionAccount(null)
                inputRef.current?.focus()
              }}
              aria-label={t('promptBar.newOrder')}
              title={t('promptBar.newOrder')}
              className="text-foreground/50 hover:text-foreground hover:bg-foreground/10 mb-0.5 -ml-1 flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors"
            >
              <SquarePen className="size-3.5" />
            </button>
          ) : (
            <Sparkles className="text-foreground/40 mb-1.5 size-3.5 shrink-0" />
          )}
          <div className="relative min-w-0 flex-1 self-center">
            {effectiveMentionAccount && (
              <span
                ref={chipRef}
                className="bg-foreground/10 text-foreground absolute top-0 left-0 z-10 flex h-6 max-w-32 items-center gap-1 rounded-full px-1 text-xs font-medium"
              >
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={isPending}
                      className="h-6 min-w-0 gap-1 px-0.5 text-xs"
                      title={!mentionAccount ? t('defaultAccount') : undefined}
                      aria-label={`${effectiveMentionAccount.name}${!mentionAccount ? ` (${t('defaultAccount')})` : ''}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExchangeIcon
                        exchange={tradeVenueToIconKey(effectiveMentionAccount.tradeVenue)}
                        className="size-3.5 shrink-0 rounded-full"
                      />
                      <span className="min-w-0 truncate">{effectiveMentionAccount.name}</span>
                      <ChevronDown className="size-3 shrink-0" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()}>
                    <OverlayScrollY scrollButtons scrollerClassName="max-h-56">
                      {eligibleAccounts.map((account) => (
                        <DropdownMenuItem
                          key={account.accountId}
                          onSelect={() => {
                            setMentionAccount(
                              account.accountId === defaultAccount?.accountId ? null : account
                            )
                          }}
                          className="gap-2 text-xs"
                        >
                          <ExchangeIcon
                            exchange={tradeVenueToIconKey(account.tradeVenue)}
                            className="size-3.5 shrink-0 rounded-full"
                          />
                          <span className="flex-1">{account.name}</span>
                          {account.accountId === defaultAccount?.accountId && (
                            <Badge variant="palette" className="ml-auto shrink-0">
                              Default
                            </Badge>
                          )}
                        </DropdownMenuItem>
                      ))}
                    </OverlayScrollY>
                  </DropdownMenuContent>
                </DropdownMenu>
                {mentionAccount && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      clearMention()
                      inputRef.current?.focus()
                    }}
                    aria-label={t('promptBar.removeMention', {
                      account: effectiveMentionAccount.name,
                    })}
                    className="text-foreground/50 hover:text-foreground flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-full"
                  >
                    <X className="size-3" />
                  </button>
                )}
              </span>
            )}
            <textarea
              ref={inputRef}
              rows={1}
              value={text}
              onChange={(e) => {
                setText(e.target.value)
                readMention(e.target)
              }}
              onSelect={(e) => readMention(e.currentTarget)}
              onBlur={() => setMention(null)}
              onKeyDown={onKeyDown}
              disabled={isPending}
              // Transcripts replace the field while dictating; typing then would fight them.
              readOnly={isDictating}
              // No `placeholder` prop: useTypewriterPlaceholder owns that attribute.
              aria-label={t('promptBar.placeholder')}
              aria-autocomplete="list"
              style={chipIndent ? { textIndent: chipIndent } : undefined}
              // While empty the field is one line, so the placeholder (which the content-sized
              // field also sizes to) can never wrap and bounce the pill — a phrase too wide for
              // the line is clipped instead. Typed text wraps normally.
              className={cn(
                'placeholder:text-foreground/40 text-foreground block field-sizing-content max-h-24 min-h-6 w-full min-w-0 resize-none bg-transparent py-0.5 text-[13px] leading-5 outline-none disabled:opacity-60',
                text === '' && 'overflow-hidden whitespace-nowrap placeholder:text-ellipsis'
              )}
            />
          </div>
          {/* One trailing slot, toggling like the assistant composer's: spinner while the
              backend answers, stop while dictating, mic while empty, send once there's text. */}
          {isPending ? (
            <span
              aria-hidden="true"
              className="bg-foreground/60 flex size-7 shrink-0 items-center justify-center rounded-full"
            >
              <Loader2 className="text-cta-foreground size-3.5 animate-spin" />
            </span>
          ) : isDictating ? (
            <button
              type="button"
              onClick={stopDictation}
              aria-label={t('promptInput.stopDictation')}
              title={t('promptInput.stopDictation')}
              className={MIC_LIVE_CLASS}
              style={MIC_LIVE_STYLE}
            >
              <AudioLines className="size-3.5" />
            </button>
          ) : !hasText ? (
            <button
              type="button"
              onClick={startDictation}
              aria-label={t('promptInput.startDictation')}
              title={t('promptInput.startDictation')}
              className={MIC_IDLE_CLASS}
            >
              <AudioLines className="size-3.5" />
            </button>
          ) : (
            <button
              type="submit"
              aria-label={t('promptInput.send')}
              title={t('promptInput.send')}
              className={TRAILING_BUTTON_CLASS}
            >
              <ArrowUp className="size-3.5" />
            </button>
          )}
        </form>
        {mention && (
          <div
            role="listbox"
            aria-label={t('promptBar.mentionAccounts')}
            className="menu-surface absolute inset-x-0 top-full z-50 mt-1 rounded-sm py-1"
          >
            <p className="text-foreground/40 px-3 pt-0.5 pb-1 text-[10px] uppercase">
              {t('promptBar.mentionAccounts')}
            </p>
            {candidates.length === 0 ? (
              <p className="text-foreground/50 px-3 py-1.5 text-xs">
                {t('promptBar.mentionNoMatch')}
              </p>
            ) : (
              candidates.map((candidate, index) => (
                <button
                  key={candidate.accountId}
                  type="button"
                  role="option"
                  aria-selected={index === highlight}
                  // mousedown, not click: keep the field focused so the pick lands in it.
                  onMouseDown={(e) => {
                    e.preventDefault()
                    pickAccount(candidate)
                  }}
                  onMouseEnter={() => setHighlight(index)}
                  className={cn(
                    'text-foreground flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left text-xs',
                    index === highlight && 'bg-foreground/5'
                  )}
                >
                  <ExchangeIcon
                    exchange={tradeVenueToIconKey(candidate.tradeVenue)}
                    className="size-3.5 shrink-0 rounded-full"
                  />
                  <span className="min-w-0 flex-1 truncate">{candidate.name}</span>
                  {candidate.accountId === defaultAccount?.accountId && (
                    <span className="bg-foreground/40 size-1.5 shrink-0 rounded-full" />
                  )}
                </button>
              ))
            )}
          </div>
        )}
      </div>
      {status.kind !== 'idle' && (
        <div className="mt-1.5 flex items-start gap-2 text-xs leading-snug">
          <p
            className={cn(
              'min-w-0 flex-1 leading-snug',
              status.kind === 'error' ? 'text-chart-red' : 'text-foreground/60'
            )}
          >
            {status.kind === 'loaded'
              ? t(status.priceDropped ? 'promptBar.loadedSetPrice' : 'promptBar.loaded')
              : status.kind === 'plan'
                ? t('promptBar.planUnsupported')
                : status.message || t('promptBar.failed')}
          </p>
          {/* One trailing group, sized to the note's first line (h-[1lh]) and centred on it:
              the thumbs (on the reply that produced this note, like the chat's own replies)
              and the close button, both the same 20px Action so they line up. MessageActions
              is memoized on isLoading only, so it's keyed per turn to clear the previous
              reply's "flagged" state. */}
          <div className="flex h-[1lh] shrink-0 items-center gap-1">
            {feedback && status.kind !== 'error' && (
              <MessageActions
                key={`${feedback.chatId}:${feedback.turnIndex}`}
                chatId={feedback.chatId}
                message={feedback.message}
                isLoading={false}
                turnIndex={feedback.turnIndex}
                feedbackOnly
              />
            )}
            <Action onClick={dismiss} label={t('promptBar.dismiss')}>
              <X className="text-foreground/60 size-3" />
            </Action>
          </div>
        </div>
      )}
      {status.kind === 'reply' && status.choice && (
        // Keyed per turn: a follow-up question starts with nothing picked.
        <LiveChoicePicker
          key={feedback ? `${feedback.chatId}:${feedback.turnIndex}` : undefined}
          className="mt-2"
          request={status.choice}
          account={status.account}
          pending={isPending}
          onAnswer={(reply) => {
            refocusRef.current = true
            void submit(reply, { account: status.mention })
          }}
        />
      )}
    </div>
  )
}
